import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { IntensityPicker, LanguageSelect, PersonalityPicker } from "@/components/dashboard/StyleControls";
import { useAuth } from "@/context/AuthContext";
import { ProfileSection } from "@/components/settings/ProfileSection";
import { DetailsSection } from "@/components/settings/DetailsSection";
import { DevicesSection } from "@/components/settings/DevicesSection";
import { ConsentSection } from "@/components/settings/ConsentSection";
import { api, errMsg } from "@/lib/api";
import { VOICES } from "@/lib/config";

const RETENTION = [
  ["none", "Don't save", "Your transcript stays only in this browser tab and is never stored on our servers."],
  ["1h", "1 hour", "Keep the current conversation for an hour so you can come back to it."],
  ["24h", "24 hours", "Keep the current conversation for a day."],
];

const Section = ({ title, children, testId }) => (
  <section className="panel p-6 sm:p-8 space-y-6" data-testid={testId}>
    <h2 className="text-lg font-semibold">{title}</h2>
    {children}
  </section>
);

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setPrefs((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/user-preferences").then(({ data }) => setPrefs(data)).catch((e) => toast.error(errMsg(e))); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/user-preferences", prefs);
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(errMsg(e, "Couldn't save settings."));
    } finally {
      setSaving(false);
    }
  };
  const clearStored = async () => {
    try { await api.delete("/conversation-session"); toast.success("Stored conversation deleted."); } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteAccount = async () => {
    try {
      await api.delete("/account");
      await logout("local");
      toast.success("Your account and data were deleted.");
      navigate("/");
    } catch (e) {
      toast.error(errMsg(e, "Couldn't delete your account. Please retry."));
    }
  };

  if (!prefs) return <div className="min-h-screen"><Header /><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></div>;

  return (
    <div className="min-h-screen" data-testid="settings-page">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <Link to="/app" data-testid="settings-back-link" className="text-sm text-primary inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" />Back to generator</Link>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Settings & privacy</h1>

        <Section title="Default style" testId="settings-defaults">
          <div className="space-y-2"><div className="text-sm font-medium">Language</div><LanguageSelect value={prefs.language} onChange={set("language")} testId="settings-language-select" /></div>
          <div className="space-y-2"><div className="text-sm font-medium">Flirting intensity</div><IntensityPicker value={prefs.intensity} onChange={set("intensity")} prefix="settings-intensity" /></div>
          <div className="space-y-2"><div className="text-sm font-medium">Personality</div><PersonalityPicker value={prefs.personality} onChange={set("personality")} prefix="settings-personality" /></div>
          <div className="space-y-2">
            <label htmlFor="my-vibe" className="text-sm font-medium">My Vibe <span className="font-normal text-muted-foreground">(optional)</span></label>
            <Textarea id="my-vibe" data-testid="settings-my-vibe-input" value={prefs.my_vibe || ""} onChange={(e) => set("my_vibe")(e.target.value)} maxLength={300} rows={3}
              placeholder="How do you usually text? e.g. dry humour, lowercase, lots of 'haha', into football and filmy references" className="rounded-xl text-sm" />
            <p className="text-xs text-muted-foreground">Used as a style hint for every reply and Rizz Mode. Avoid sharing sensitive personal details.</p>
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium">Read-aloud voice</div>
            <Select value={prefs.tts_voice} onValueChange={set("tts_voice")}>
              <SelectTrigger data-testid="settings-voice-select" className="h-11 rounded-xl w-full sm:w-60"><SelectValue /></SelectTrigger>
              <SelectContent>{VOICES.map((v) => <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Voices are optimised for English; Hindi and Hinglish will have an English accent.</p>
          </div>
        </Section>

        <Section title="Privacy & data" testId="settings-privacy">
          <div className="space-y-3">
            <div className="text-sm font-medium">Keep my current conversation</div>
            <RadioGroup value={prefs.retention} onValueChange={set("retention")} className="space-y-2">
              {RETENTION.map(([id, label, desc]) => (
                <label key={id} htmlFor={`ret-${id}`} className={`flex gap-3 rounded-2xl border p-4 cursor-pointer transition-colors ${prefs.retention === id ? "border-primary bg-primary/5" : "border-border"}`}>
                  <RadioGroupItem value={id} id={`ret-${id}`} data-testid={`settings-retention-${id}`} className="mt-0.5" />
                  <span><span className="block text-sm font-semibold">{label}</span><span className="block text-xs text-muted-foreground">{desc}</span></span>
                </label>
              ))}
            </RadioGroup>
            <p className="text-xs text-muted-foreground">Screenshots are never stored. Generated replies are never stored.</p>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div><div className="text-sm font-medium">Turn off optional analytics</div><div className="text-xs text-muted-foreground">We don't run third-party analytics today; this keeps any future optional analytics off for you.</div></div>
            <Switch data-testid="settings-analytics-switch" checked={prefs.analytics_opt_out} onCheckedChange={set("analytics_opt_out")} />
          </div>
          <button data-testid="settings-clear-stored-button" onClick={clearStored} className="h-10 px-4 rounded-full border border-border text-sm font-medium">Delete stored conversation now</button>
        </Section>

        <button data-testid="settings-save-button" onClick={save} disabled={saving} className="w-full h-12 rounded-full brand-gradient text-white font-semibold disabled:opacity-50">
          {saving ? "Saving…" : "Save settings"}
        </button>

        <DetailsSection />
        <ProfileSection />
        <DevicesSection />
        <ConsentSection />

        <Section title="Account" testId="settings-account">
          <div className="text-sm"><span className="text-muted-foreground">Signed in with Google as</span> <span className="font-semibold" data-testid="settings-account-email">{user?.email}</span></div>
          <button data-testid="settings-logout-button" onClick={async () => { await logout(); navigate("/"); }} className="h-10 px-4 rounded-full border border-border text-sm font-medium mr-2">Log out</button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="settings-delete-account-button" className="h-10 px-4 rounded-full border border-rose-500/50 text-rose-500 text-sm font-semibold inline-flex items-center gap-2 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" />Delete account and data</button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                <AlertDialogDescription>This permanently and immediately deletes your profile, gender information, preferences, consent records, usage history and any stored conversation. This can't be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="delete-account-confirm" onClick={deleteAccount} className="bg-rose-600 hover:bg-rose-700">Delete everything</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Section>
      </main>
    </div>
  );
}
